import { Box, Grid, Paper, styled, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import { Button, Typography } from "@platform/design-system";

import AddCurrentCoverages from "../../components/addCoverages/addCurrentCoverages";
import NewTextInfo from "../../components/newTextInfo";
import { CLIENT_FORM } from "../../constants/client";
import { Client } from "../../types/clientTab";
import { CoveragesData } from "../../utils";

import EditClientDialog from "./editClientDialog";
import { ClientDetailsContext } from "./types";

const { CLIENT_ID, CLIENT_TYPE } = CLIENT_FORM;

interface Header {
  id: string;
  label: string;
  isSortable: boolean;
}

export const headers: Header[] = [
  {
    id: "product",
    label: "Product",
    isSortable: false,
  },
  {
    id: "carrier",
    label: "Carrier",
    isSortable: false,
  },
  {
    id: "carrierTierLevel",
    label: "Carrier Tier",
    isSortable: false,
  },
  {
    id: "premium",
    label: "Premium",
    isSortable: false,
  },
  {
    id: "renewalDate",
    label: "Renewal Date",
    isSortable: false,
  },
];

const StyledGridItem = styled(Grid)(() => ({
  padding: "0 !important",
}));

// eslint-disable-next-line complexity
const Details = (): React.JSX.Element => {
  const {
    client,
    clientId,
    isAdmin,
    isAnalyst,
    coverages,
    setCoverages,
    isConsultant,
  } = useOutletContext<ClientDetailsContext>();
  const [clientToEdit, setClientToEdit] = useState<Client>();
  const isProspect = client.type === "prospect";

  function handleAddCoverages(newCoverages: CoveragesData[]): void {
    setCoverages((old) => [...old, ...newCoverages]);
  }

  function joinText(...values: (string | undefined)[]): string {
    return values.filter(Boolean).join(", ");
  }

  const sections = [
    {
      title: "Client Information",
      data: [
        { text: "Client Name", value: client?.name },
        {
          text: "Headquarters Address",
          value: joinText(
            client?.address?.addressLine1,
            client?.address?.addressLine2,
          ),
          extraSubtitle: joinText(
            client?.address?.city,
            client?.address?.state,
            client?.address?.zipCode,
          ),
        },
        {
          text: "Situs State",
          value: client?.situsStateName || client?.situsState,
          tooltip:
            "The state where a policy contract is issued or location of the policy owner.",
        },
        { text: "Eligible Employees", value: client?.numberOfEmployees },
        {
          text: "Producer",
          value:
            (client?.producerFirstName || "") +
            (client?.producerLastName ? ` ${client.producerLastName}` : ""),
          extraSubtitle: client?.producerEmail || "",
        },
        ...(client?.brokerClientInfoConfig?.clientExecutive
          ? [
              {
                text: "Client Executive",
                value: client?.additionalInfo?.clientExecutive,
              },
            ]
          : []),
        ...(client?.brokerClientInfoConfig?.practice
          ? [
              {
                text: "Practice",
                value: client?.additionalInfo?.practice?.name,
              },
            ]
          : []),
        ...(client?.brokerClientInfoConfig?.corporateEntity
          ? [
              {
                text: "Corporate Entity",
                value: client?.additionalInfo?.corporateEntity?.name,
              },
            ]
          : []),
        ...(client?.brokerClientInfoConfig?.officeLocation
          ? [
              {
                text: "Office Location",
                value: client?.additionalInfo?.officeLocation?.name,
              },
            ]
          : []),
        ...(client?.brokerClientInfoConfig?.onedigitalOffice
          ? [
              {
                text: "ONEDIGITAL Office",
                value: client?.additionalInfo?.onedigitalOffice,
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <>
      <Grid container size={{ sm: 12 }} spacing={3} px={6} py={4.5}>
        {sections.map((section) => (
          <React.Fragment key={`section-${section.title}`}>
            <StyledGridItem
              size={12}
              display="flex"
              justifyContent="space-between"
            >
              <Typography
                variant="display-small"
                style={{
                  fontWeight: "400",
                  letterSpacing: "0px",
                }}
              >
                {sections[0].title}
              </Typography>
              {!isAdmin && !isAnalyst && !isConsultant ? (
                <Box>
                  <Button
                    variant="tertiary"
                    onClick={() =>
                      setClientToEdit({ ...client, [CLIENT_ID]: clientId })
                    }
                  >
                    Edit
                  </Button>
                </Box>
              ) : null}
            </StyledGridItem>
            <Grid container component={Paper} p={3} mb={3} size={12}>
              <StyledGridItem size={12} display="flex">
                <Grid container size={12}>
                  {section.data.map((row, _) => (
                    <Grid
                      key={`${section.title}-${row.text}`}
                      size={{ xs: 6, md: 2.4 }}
                    >
                      <ItemText
                        title={row.text}
                        subtitle={row.value ? `${row.value}` : "-"}
                        extraSubtitle={row.extraSubtitle || ""}
                        tooltip={row?.tooltip}
                      />
                    </Grid>
                  ))}
                </Grid>
              </StyledGridItem>
            </Grid>
          </React.Fragment>
        ))}
        <StyledGridItem size={12}>
          <AddCurrentCoverages
            brokerType={client.brokerType}
            brokerHasMultipleStrategicTiers={
              client.brokerHasMultipleStrategicTiers
            }
            coverages={coverages}
            onAddCoverages={handleAddCoverages}
            clientId={clientId}
            isAdmin={isAdmin}
            isAnalyst={isAnalyst}
            hideNonNACols
            showConnectedCarriers={!isProspect}
            embeded
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            clientType={client[CLIENT_TYPE]}
            client={client}
            isConsultant={isConsultant}
          />
        </StyledGridItem>
      </Grid>
      <EditClientDialog
        client={clientToEdit}
        onClose={() => setClientToEdit(undefined)}
      />
    </>
  );
};

interface ItemTextProps {
  title: string;
  subtitle: string;
  extraSubtitle?: string;
  tooltip?: string;
}

const ItemText: React.FC<ItemTextProps> = ({
  title,
  subtitle,
  extraSubtitle,
  tooltip = "",
  ...rest
}) => (
  <Box mt={2} sx={{ padding: "0.5rem" }} {...rest}>
    {tooltip ? (
      <NewTextInfo tooltipMsg={tooltip}>
        <Typography variant="body-large" mb={0.5}>
          <strong>{title}</strong>
        </Typography>
      </NewTextInfo>
    ) : (
      <Typography variant="body-large" mb={0.5}>
        <strong>{title}</strong>
      </Typography>
    )}
    <EllipsisTooltip text={subtitle} />
    {extraSubtitle && <EllipsisTooltip text={extraSubtitle} />}
  </Box>
);
const EllipsisTooltip = ({ text }: { text: string }): React.JSX.Element => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsEllipsisActive(element.scrollWidth > element.clientWidth);
    }
  }, [text]);

  return (
    <Tooltip title={isEllipsisActive ? text : ""}>
      <Typography
        ref={textRef}
        variant="body-large"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};
export { Details };
